/*
 * Yoni HerBiome — gentle, motion-safe reveals.
 * Adds .yoni-in to [data-reveal] elements as they scroll into view,
 * honouring an optional per-element delay via data-reveal="<ms>", and to
 * [data-reveal-stagger="<ms>"] containers, whose children then come in one
 * after the other, <ms> apart (CSS in yoni-tokens.css).
 * All motion is additionally gated in CSS by body.yoni-motion-off and
 * prefers-reduced-motion, so this script never fights those settings.
 * Re-reveals content when the theme editor re-renders a section.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // [data-reveal-hook]: only told when it comes into view (.yoni-in), for a motion of its own.
  var REVEAL = '[data-reveal], [data-reveal-stagger], [data-reveal-hook]';
  var observer = null;
  var noMotion = false;

  // One off switch for every Yoni script that moves things: the theme setting or the OS.
  window.yoniCalm = function () {
    return document.body.classList.contains('yoni-motion-off') ||
      !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  function setup() {
    var motionOff = document.body.classList.contains('yoni-motion-off');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    noMotion = motionOff || reduce || !('IntersectionObserver' in window);
    // CSS may hold a [data-reveal-hook] motion's first frame only when the observer runs.
    if (!noMotion) document.documentElement.classList.add('yoni-motion-js');
    if (!noMotion) {
      observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var el = entry.target;
            var delay = parseInt(el.getAttribute('data-reveal'), 10);
            var step = parseInt(el.getAttribute('data-reveal-stagger'), 10);
            if (delay > 0) el.style.animationDelay = delay + 'ms';
            if (step > 0) el.style.setProperty('--yoni-step', step + 'ms');
            el.classList.add('yoni-in');
            obs.unobserve(el);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
  }

  function reveal(scope) {
    var targets = (scope || document).querySelectorAll(REVEAL);
    if (!targets.length) return;
    if (noMotion || !observer) {
      targets.forEach(function (el) { el.classList.add('yoni-in'); });
      return;
    }
    targets.forEach(function (el) { observer.observe(el); });
  }

  // Content swapped in after load never reached the observer and stayed at
  // opacity 0 — e.g. a section re-rendered by `shopify theme dev` hot reload,
  // which fires no shopify:section:load. Hand anything new to the observer.
  function revealAdded(mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches(REVEAL)) reveal(node.parentNode);
        else if (node.querySelector(REVEAL)) reveal(node);
        initDetails(node);
      });
    });
  }

  // ---- Cards that open and close: the <details> inside a [data-yoni-details] list (the product
  // page details cards, the FAQ). Every card travels from where it is on screen to its new
  // place and size instead of jumping (on the product page an open card takes the whole row,
  // so the grid re-flows), the opened text softens in, a closing card keeps its text until it
  // has shrunk. The buy box passes and curve: read every box, make the change, read the new
  // boxes, then move; a second click mid-motion starts from where the cards are.
  // Motion off / reduced / no JS: the native <details> toggle. Styles that depend on the state
  // key on [open]:not(.is-closing).
  var MOVE = 420, SHUT = 320, EASE = 'cubic-bezier(0.32, 0.72, 0, 1)', OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
  function initDetails(scope) {
    var lists = [].slice.call(scope.querySelectorAll('[data-yoni-details]'));
    if (scope.matches && scope.matches('[data-yoni-details]')) lists.push(scope);
    lists.forEach(function (list) {
      if (list._yoniDetails) return;
      list._yoniDetails = true;
      var items = [].slice.call(list.children).filter(function (el) { return el.matches('details'); });
      var live = [];
      function body(el) { return el.querySelector(':scope > :not(summary)'); }

      function boxes() {
        var g = list.getBoundingClientRect();
        return items.map(function (el) {
          var r = el.getBoundingClientRect(), p = el.open ? body(el) : null;
          return { x: r.left - g.left, y: r.top - g.top, w: r.width, h: r.height, pw: p ? p.getBoundingClientRect().width : 0 };
        });
      }
      function clear() {
        live.forEach(function (a) { a.cancel(); });
        live = [];
        ['position', 'height', 'overflowAnchor'].forEach(function (k) { list.style[k] = ''; });
        items.forEach(function (el) {
          ['position', 'margin', 'left', 'top', 'width', 'height', 'zIndex'].forEach(function (k) { el.style[k] = ''; });
          var p = body(el);
          if (p) p.style.width = '';
        });
      }
      // The end of a motion: a closing card lets its text go.
      function settle() {
        clear();
        items.forEach(function (el) {
          if (el.classList.contains('is-closing')) { el.classList.remove('is-closing'); el.open = false; }
        });
      }

      items.forEach(function (d) {
        d.querySelector('summary').addEventListener('click', function (e) {
          if (!list.animate || window.yoniCalm()) { settle(); return; }
          e.preventDefault();
          // 1. Read: every box as it is on screen, a motion still running included.
          var first = boxes(), g0 = list.getBoundingClientRect().height;
          // 2. Write: stop where it is, make the change.
          clear();
          var opening = !d.open || d.classList.contains('is-closing');
          if (opening) { d.classList.remove('is-closing'); d.open = true; } else d.classList.add('is-closing');
          // 3. Read the new boxes; a closing card is measured without its text, then keeps it.
          var closing = items.filter(function (el) { return el.classList.contains('is-closing'); });
          closing.forEach(function (el) { el.open = false; });
          var last = boxes(), g1 = list.getBoundingClientRect().height;
          closing.forEach(function (el) { el.open = true; });
          // 4. Move: out of the flow at the new place, from the old box to the new one.
          var dur = opening ? MOVE : SHUT;
          list.style.position = 'relative';
          list.style.height = g1 + 'px';
          list.style.overflowAnchor = 'none';
          items.forEach(function (el, i) {
            var f = first[i], l = last[i];
            el.style.position = 'absolute';
            el.style.margin = '0';
            el.style.left = l.x + 'px';
            el.style.top = l.y + 'px';
            el.style.width = l.w + 'px';
            el.style.height = l.h + 'px';
            if (el === d && opening) el.style.zIndex = '1'; // a growing card passes over the others, a closing one under
            // The text keeps its widest width, so it never re-wraps while its box changes size.
            var p = el.open ? body(el) : null;
            if (p) p.style.width = Math.max(f.pw, l.pw) + 'px';
            if (Math.abs(f.x - l.x) + Math.abs(f.y - l.y) + Math.abs(f.w - l.w) + Math.abs(f.h - l.h) < 1) return;
            live.push(el.animate([
              { transform: 'translate(' + (f.x - l.x) + 'px, ' + (f.y - l.y) + 'px)', width: f.w + 'px', height: f.h + 'px' },
              { transform: 'none', width: l.w + 'px', height: l.h + 'px' }
            ], { duration: dur, easing: EASE }));
          });
          var panel = body(d);
          if (panel) {
            live.push(opening
              ? panel.animate([{ opacity: 0.25, filter: 'blur(2px)' }, { opacity: 1, filter: 'none' }], { duration: 320, delay: 80, easing: OUT, fill: 'backwards' })
              : panel.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: OUT, fill: 'forwards' }));
          }
          var a = list.animate([{ height: g0 + 'px' }, { height: g1 + 'px' }], { duration: dur, easing: EASE });
          live.push(a);
          a.onfinish = function () { if (live.indexOf(a) >= 0) settle(); };
        });
      });
    });
  }

  // ---- Goo: the chosen item of a group (the buy box tiles and purchase rows, tabs, option cards,
  // the cart drawer's plan pills) is marked by one soft shape under the labels, never carrying
  // text. On a change it moves like a jelly: its back edge and its front edge each make ONE smooth
  // move from the old item to the new one, the back with a head start sized so that half-way the
  // mass is a short jelly 1.5 x its height long (a bit over half an item tall, a thinner waist,
  // a round head and tail). It never splits and never stops before it lands. While it moves an SVG
  // filter blurs its mask and cuts it back to a sharp edge, so its parts melt together, and paints
  // the group's colours (--goo-fill, --goo-ring + --goo-ring-w, --goo-halo + --goo-halo-w, set in
  // the group's CSS); at rest the filter is off and plain CSS draws the same look. A label takes its
  // chosen colour (.is-lit) while the shape covers half of it. A second change mid-way starts from
  // where the shape is; the item it goes to is read every frame, so it follows a row that grows.
  // Motion off / reduced: it jumps. opts: items (selector), isOn(el), from (index chosen before,
  // for a group drawn anew: the shape starts there), watch (follow aria-selected / aria-pressed),
  // still() (true: jump, e.g. before the first paint).
  var GOO = 920, GOO_REST = 0.6, GOO_PAD = 18, SVGNS = 'http://www.w3.org/2000/svg', gooDefs = null, gooN = 0;
  function bezier(x1, y1, x2, y2) {
    function at(t, p1, p2) { return ((1 - 3 * p2 + 3 * p1) * t + 3 * p2 - 6 * p1) * t * t + 3 * p1 * t; }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      var lo = 0, hi = 1, t = x;
      for (var i = 0; i < 24; i++) { if (at(t, x1, x2) < x) lo = t; else hi = t; t = (lo + hi) / 2; }
      return at(t, y1, y2);
    };
  }
  var SINE = bezier(0.37, 0, 0.63, 1), SWAY = bezier(0.65, 0, 0.35, 1);
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function svgEl(name, attrs, parent) {
    var e = document.createElementNS(SVGNS, name);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
  }
  // The group's filter: blur the mask, cut it back at half strength (straight edges stay where
  // they are, close parts bridge), then paint halo, ring and fill. Built on the first move.
  function gooFilter(group) {
    if (!gooDefs) {
      gooDefs = svgEl('svg', { width: '0', height: '0', 'aria-hidden': 'true', focusable: 'false' });
      gooDefs.style.position = 'absolute';
      document.body.appendChild(gooDefs);
    }
    var cs = getComputedStyle(group);
    function v(n) { return cs.getPropertyValue(n).trim(); }
    var id = 'yoni-goo-' + (++gooN);
    var f = svgEl('filter', { id: id, x: '-20%', y: '-20%', width: '140%', height: '140%', 'color-interpolation-filters': 'sRGB' }, gooDefs);
    var blur = svgEl('feGaussianBlur', { in: 'SourceAlpha', stdDeviation: GOO_REST, result: 'b' }, f);
    svgEl('feFuncA', { type: 'linear', slope: '24', intercept: '-11.5' }, svgEl('feComponentTransfer', { in: 'b', result: 'sil' }, f));
    var layers = [], body = 'sil';
    if (v('--goo-halo')) {
      svgEl('feMorphology', { in: 'sil', operator: 'dilate', radius: v('--goo-halo-w') || '4', result: 'hd' }, f);
      svgEl('feFlood', { 'flood-color': v('--goo-halo') }, f);
      svgEl('feComposite', { in2: 'hd', operator: 'in', result: 'halo' }, f);
      layers.push('halo');
    }
    if (v('--goo-ring')) {
      svgEl('feFlood', { 'flood-color': v('--goo-ring') }, f);
      svgEl('feComposite', { in2: 'sil', operator: 'in', result: 'ring' }, f);
      svgEl('feMorphology', { in: 'sil', operator: 'erode', radius: v('--goo-ring-w') || '1.5', result: 'inner' }, f);
      layers.push('ring');
      body = 'inner';
    }
    svgEl('feFlood', { 'flood-color': v('--goo-fill') || '#000' }, f);
    svgEl('feComposite', { in2: body, operator: 'in', result: 'fill' }, f);
    layers.push('fill');
    var m = svgEl('feMerge', {}, f);
    layers.forEach(function (n) { svgEl('feMergeNode', { in: n }, m); });
    return { url: 'url(#' + id + ')', blur: blur };
  }

  window.yoniGoo = function (group, opts) {
    if (!group) return null;
    if (group._yoniGoo) return group._yoniGoo;
    var items = [].slice.call(group.querySelectorAll(opts.items));
    if (!items.length) return null;
    var layer = document.createElement('span');
    layer.className = 'yoni-goo';
    layer.setAttribute('aria-hidden', 'true');
    group.insertBefore(layer, group.firstChild);
    group.classList.add('has-goo');
    var F = null, body = null, at = null, run = null, melts = [], raf = 0, follow = 0;
    var gel = 20, stick = 4, blurNow = GOO_REST, radii = [], lastW = group.clientWidth;

    function blob() { var e = document.createElement('i'); layer.appendChild(e); return { el: e, r: null }; }
    function kill(b) { if (b) b.el.remove(); }
    function put(b, r) {
      b.r = r;
      b.el.style.transform = 'translate(' + (r.x + GOO_PAD) + 'px, ' + (r.y + GOO_PAD) + 'px)';
      b.el.style.width = r.w + 'px';
      b.el.style.height = r.h + 'px';
      b.el.style.borderRadius = Math.max(0, Math.min(r.r, r.h / 2, r.w / 2)) + 'px';
    }
    function rectOf(el) {
      var i = items.indexOf(el);
      return { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight, r: radii[i] || 0 };
    }
    function chosen() { return items.filter(opts.isOn)[0]; }
    // The jelly is a bit over half an item tall; how sticky it gets in motion (the blur) is sized
    // from it, so its parts always stay joined.
    function tune() {
      radii = items.map(function (el) { return parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0; });
      var h = Math.min.apply(null, items.map(function (el) { return el.offsetHeight; }));
      gel = Math.max(14, Math.min(56, h * 0.55));
      stick = gel / 4.4;
    }
    function setBlur(v) { blurNow = v; if (F) F.blur.setAttribute('stdDeviation', v.toFixed(2)); }
    function filterOn(on) {
      if (on && !F) F = gooFilter(group);
      layer.classList.toggle('is-moving', on);
      layer.style.filter = on ? F.url : '';
      if (!on) setBlur(GOO_REST);
    }
    // A label takes the chosen colour while the shape covers half of it.
    function light(shapes, boxes) {
      var U = null;
      shapes.forEach(function (r) {
        if (!r) return;
        U = U ? { x0: Math.min(U.x0, r.x), y0: Math.min(U.y0, r.y), x1: Math.max(U.x1, r.x + r.w), y1: Math.max(U.y1, r.y + r.h) } : { x0: r.x, y0: r.y, x1: r.x + r.w, y1: r.y + r.h };
      });
      items.forEach(function (el, i) {
        var R = boxes[i], w = U ? Math.min(U.x1, R.x + R.w) - Math.max(U.x0, R.x) : 0, h = U ? Math.min(U.y1, R.y + R.h) - Math.max(U.y0, R.y) : 0;
        el.classList.toggle('is-lit', w > 0 && h > 0 && w * h >= 0.5 * R.w * R.h);
      });
    }
    function stop() {
      cancelAnimationFrame(raf);
      raf = 0;
      if (run) { kill(run.head); kill(run.tail); run = null; }
      melts.forEach(function (m) { kill(m.b); });
      melts = [];
    }
    function scaled(R, k) { var cx = R.x + R.w / 2, cy = R.y + R.h / 2; return { x: cx - R.w * k / 2, y: cy - R.h * k / 2, w: R.w * k, h: R.h * k, r: R.r * k }; }

    // One jelly from the rectangle F0 to the item el.
    function jelly(F0, el, now) {
      var D = gel, Lmin = 1.5 * D, to0 = rectOf(el);
      var horiz = Math.abs(to0.x + to0.w / 2 - F0.x - F0.w / 2) >= Math.abs(to0.y + to0.h / 2 - F0.y - F0.h / 2);
      function ax(R) { return horiz ? { a0: R.x, a1: R.x + R.w, c0: R.y, c1: R.y + R.h } : { a0: R.y, a1: R.y + R.h, c0: R.x, c1: R.x + R.w }; }
      function rect(a0, a1, cc, th, r) {
        var lo = Math.min(a0, a1), hi = Math.max(a0, a1);
        return horiz ? { x: lo, y: cc - th / 2, w: hi - lo, h: th, r: r } : { x: cc - th / 2, y: lo, w: th, h: hi - lo, r: r };
      }
      function along(a, cc) { return horiz ? { x: a, y: cc } : { x: cc, y: a }; }
      function dot(c, d) { return { x: c.x - d / 2, y: c.y - d / 2, w: d, h: d, r: d / 2 }; }
      var A = ax(F0), B0 = ax(to0), dir = B0.a0 + B0.a1 > A.a0 + A.a1 ? 1 : -1;
      function state(p, lag, B, to) {
        var e = SINE(p);
        var lead = lerp(dir > 0 ? A.a1 : A.a0, dir > 0 ? B.a1 : B.a0, SINE(clamp01((p - lag) / (1 - lag))));
        var trail = lerp(dir > 0 ? A.a0 : A.a1, dir > 0 ? B.a0 : B.a1, SINE(clamp01(p / (1 - lag))));
        var full = lerp(A.a1 - A.a0, B.a1 - B.a0, e), len = Math.abs(lead - trail);
        var c = full > Lmin ? clamp01((full - len) / (full - Lmin)) : 0;
        var th = lerp(lerp(A.c1 - A.c0, B.c1 - B.c0, e), D, c);
        var cc = lerp(A.c0 + A.c1, B.c0 + B.c1, e) / 2;
        return { lead: lead, trail: trail, len: len, th: th, cc: cc, c: c, r: lerp(lerp(F0.r, to.r, e), th / 2, c) };
      }
      // The back's head start: the smallest that brings the jelly down to Lmin long half-way.
      var lag = 0;
      function shortest(g) { var lo = Infinity; for (var i = 0; i <= 40; i++) lo = Math.min(lo, state(i / 40, g, B0, to0).len); return lo; }
      if (shortest(0) > Lmin) {
        var lo = 0, hi = 0.75;
        for (var k = 0; k < 18; k++) { var g = (lo + hi) / 2; if (shortest(g) > Lmin) lo = g; else hi = g; }
        lag = (lo + hi) / 2;
      }
      var s0 = blurNow, r = { el: el, t0: now, head: blob(), tail: blob() };
      // Draw progress p, the target as it is now.
      r.draw = function (p, to) {
        var st = state(p, lag, ax(to), to), th = st.th * lerp(1, 0.8, st.c), base = Math.min(st.th, st.len);
        var hd = base * lerp(0.5, 1.04, st.c), td = base * lerp(0.5, 0.96, st.c);
        put(body, rect(st.trail, st.lead, st.cc, th, Math.min(st.r, th / 2 + (1 - st.c) * st.r)));
        put(r.head, dot(along(st.lead - dir * hd / 2, st.cc), hd));
        put(r.tail, dot(along(st.trail + dir * td / 2, st.cc), td));
        setBlur(p < 0.74 ? lerp(s0, stick, SWAY(clamp01(p / 0.26))) : lerp(stick, GOO_REST, SWAY((p - 0.74) / 0.26)));
        return [body.r, r.head.r, r.tail.r];
      };
      return r;
    }

    function frame(now) {
      raf = 0;
      // Read first (the item boxes, the target), then write.
      var boxes = items.map(rectOf), shapes = [], busy = false;
      melts = melts.filter(function (m) {
        var p = clamp01((now - m.t0) / 260);
        if (p >= 1) { kill(m.b); return false; }
        put(m.b, scaled(m.R, lerp(1, 0.15, SWAY(p))));
        shapes.push(m.b.r);
        busy = true;
        return true;
      });
      if (run) {
        var p = clamp01((now - run.t0) / GOO), to = boxes[items.indexOf(run.el)];
        if (p >= 1) { kill(run.head); kill(run.tail); run = null; put(body, to); }
        else { shapes = shapes.concat(run.draw(p, to)); busy = true; }
      }
      if (!busy) filterOn(false);
      // Resting on an item whose size is still changing (a row whose price rolls): stay on it.
      if (!run && now < follow) { put(body, boxes[items.indexOf(at)]); busy = true; }
      light(shapes.concat([body.r]), boxes);
      if (busy) raf = requestAnimationFrame(frame);
    }

    function go(instant) {
      var on = chosen();
      if (!on) return;
      var now = performance.now();
      if (instant || !body || !layer.animate || window.yoniCalm() || (opts.still && opts.still())) {
        stop();
        if (!body) { body = blob(); body.el.className = 'is-body'; }
        put(body, rectOf(on));
        at = on;
        filterOn(false);
        light([body.r], items.map(rectOf));
        return;
      }
      if (run && run.el === on) return;
      if (!run && on === at) {
        follow = now + 700;
        if (!raf) raf = requestAnimationFrame(frame);
        return;
      }
      at = on;
      // Whatever else is on its way melts where it is; the body sets off from where it is.
      if (run) {
        [run.head, run.tail].forEach(function (b) { if (b.r) melts.push({ b: b, R: b.r, t0: now }); else kill(b); });
        run = null;
      }
      filterOn(true);
      run = jelly(body.r, on, now);
      if (!raf) raf = requestAnimationFrame(frame);
    }

    tune();
    var from = items[opts.from];
    if (from && !opts.isOn(from) && !window.yoniCalm()) {
      body = blob();
      body.el.className = 'is-body';
      put(body, rectOf(from));
      at = from;
      go(false);
    } else go(true);
    if (opts.watch) new MutationObserver(function () { go(false); }).observe(group, { subtree: true, attributes: true, attributeFilter: ['aria-selected', 'aria-pressed'] });
    if (window.ResizeObserver) {
      new ResizeObserver(function () { if (group.clientWidth !== lastW) { lastW = group.clientWidth; tune(); go(true); } }).observe(group);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (!run) { tune(); go(true); } });
    // Build the filter and draw it once, idle, the first time the group is on screen: the first
    // filtered frame is slow (~50 ms more in the first click on Home), and off screen nothing is drawn.
    if (!window.yoniCalm() && window.IntersectionObserver) {
      var seen = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        seen.disconnect();
        (window.requestIdleCallback || function (f) { setTimeout(f, 300); })(function () {
          if (!F) F = gooFilter(group);
          if (run) return;
          layer.style.filter = F.url;
          requestAnimationFrame(function () { requestAnimationFrame(function () { if (!run && !melts.length) layer.style.filter = ''; }); });
        });
      });
      seen.observe(group);
    }
    group._yoniGoo = { go: go };
    return group._yoniGoo;
  };

  // Horizon's phone menu (snippets/header-drawer.liquid) fades out on close, but the native
  // <details> toggle shut it in the same click: the menu vanished, and Horizon's late reset()
  // closed the next open again, so that tap was lost. The summary keeps [open]; Horizon
  // removes it itself once its exit has run. On window, in capture: before Horizon's own
  // listener (capture on document), which with motion off closes the menu at once, so a later
  // check saw it shut and the native toggle opened it again.
  window.addEventListener('click', function (e) {
    var s = e.target.closest && e.target.closest('.menu-drawer-container > summary');
    if (s && s.parentElement.open) e.preventDefault();
  }, true);

  ready(function () {
    setup();
    reveal(document);
    initDetails(document);
    new MutationObserver(revealAdded).observe(document.body, { childList: true, subtree: true });
  });

  // Theme editor: a re-rendered section's content should appear immediately; a selected
  // details block opens.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll(REVEAL).forEach(function (el) { el.classList.add('yoni-in'); });
    initDetails(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.matches && e.target.matches('[data-yoni-details] > details')) e.target.open = true;
  });
})();
